import { Component } from '@theme/component';

/**
 * ════════════════════════════════════════════════════════════════════════
 * `<bundle-reviews>` — Judge.me reviews that follow the bundle selection.
 * ────────────────────────────────────────────────────────────────────────
 * Judge.me server-renders into `.jdgm-widget[data-product-id]` and the global
 * `window.jdgm` library hydrates it. On the Bundle Builder the displayed
 * product changes on every step / product / drawer-open, so the static widget
 * is wrong after the first interaction. This component re-renders it:
 *
 *   1. Find the active product handle:
 *        - drawer instance → `bb-drawer:open` { handle }
 *        - inline instance → `bundle:update`, then ask the builder for the
 *          current step's resolved product handle.
 *   2. Fetch `/products/{handle}` once per handle (cached) and extract the
 *      rendered `.jdgm-widget` markup for that product.
 *   3. Morph it into ref="widget".
 *   4. Re-init Judge.me so the new widget hydrates (loads the review list,
 *      spinners, pagination) — via window.jdgm's public hooks with a
 *      DOM-event fallback.
 *
 * BADGE mode (`data-widget="badge"`) skips all of that and renders custom stars
 * + count synchronously — the rating ships inline on the builder config. The
 * component renders the reviewed PRODUCT only; the bundle's running price used
 * to ride along under the stars and now lives in the step header's own price
 * column (assets/bb-step.js), which prices the bundle it belongs to.
 *
 * Performance: per-handle HTML cache, a 200ms debounce on selection changes,
 * an AbortController per in-flight fetch, and a no-op when the handle is
 * unchanged. Owns ONLY `<bundle-reviews>`. Refs: widget.
 * ════════════════════════════════════════════════════════════════════════
 *
 * @typedef {object} Refs
 * @property {HTMLElement} widget
 * @extends Component<Refs>
 */
export class BundleReviewsComponent extends Component {
  requiredRefs = ['widget'];

  /** @type {Map<string, string>} handle → extracted `.jdgm-widget` outerHTML */
  #cache = new Map();

  /** The handle currently rendered, to skip redundant work. */
  #currentHandle = '';

  /** @type {AbortController | null} */
  #abort = null;

  /** @type {number | undefined} */
  #debounceId;

  connectedCallback() {
    super.connectedCallback();
    // Only treat the markup as a seed if it actually carries content. The
    // widget container ships empty now (no server-rendered Judge.me snippet),
    // so leave #currentHandle blank → the first update renders/fetches.
    this.#currentHandle = '';
    const seedHandle = this.dataset.productHandle || '';
    if (seedHandle && this.refs.widget?.innerHTML.trim()) {
      this.#currentHandle = seedHandle;
      this.#cache.set(seedHandle, this.refs.widget.innerHTML);
    }

    if (this.dataset.context === 'drawer') {
      // Drawer reviews follow whatever product the drawer opened for.
      document.addEventListener('bb-drawer:open', this.#onDrawerOpen);
    } else {
      // Inline reviews follow the builder's active step selection.
      this.#builder?.addEventListener('bundle:update', this.#onUpdate);
      this.#builder?.requestUpdate?.();
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    clearTimeout(this.#debounceId);
    this.#abort?.abort();
    document.removeEventListener('bb-drawer:open', this.#onDrawerOpen);
    this.#builder?.removeEventListener('bundle:update', this.#onUpdate);
  }

  /**
   * The host builder. Inline/drawer instances live inside <bundle-builder>;
   * the standalone page reviews section sits below it, so fall back to the
   * first builder on the page.
   * @returns {Element | null}
   */
  get #builder() {
    return this.closest('bundle-builder') || document.querySelector('bundle-builder');
  }

  /** @param {CustomEvent} event */
  #onDrawerOpen = (event) => {
    const handle = event.detail?.handle;
    if (handle) this.#schedule(handle);
  };

  #onUpdate = () => {
    const builder = this.#builder;

    // Follow the PRODUCT BEING VIEWED (draft selection of the current step),
    // not the committed bundle — reviews track the on-screen product.
    // @ts-ignore — getDraftSelection is defined on BundleBuilderComponent.
    const sel = builder?.getDraftSelection?.(builder.state?.currentStep);
    const product = sel?.product;
    if (!product) return;

    // BADGE mode: the rating + count ship inline on the config, so render our
    // custom stars synchronously — no /products/{handle} fetch, no debounce.
    // The badge updates the instant the product/colour changes.
    if (this.dataset.widget === 'badge') {
      this.#renderBadgeFromData(product);
      return;
    }

    if (product.handle) this.#schedule(product.handle);
  };

  /**
   * Instant badge render from the selection's inline rating/count. Owns the
   * whole widget: the stars are all it renders now, so there is nothing else in
   * there to preserve across a re-render.
   * @param {{productId?:string|number, rating?:number, reviewCount?:number}} product
   */
  #renderBadgeFromData(product) {
    const { widget } = this.refs;
    if (!widget) return;
    const key = String(product.productId ?? '');
    if (key && key === this.#currentHandle) return;
    this.#currentHandle = key;
    widget.innerHTML = this.#renderStars(Number(product.rating) || 0, Number(product.reviewCount) || 0);
  }

  /**
   * Debounced entry point. Skips redundant renders for the active handle.
   * @param {string} handle
   */
  #schedule(handle) {
    if (handle === this.#currentHandle) return;
    clearTimeout(this.#debounceId);
    this.#debounceId = window.setTimeout(() => this.#render(handle), 200);
  }

  /** @param {string} handle */
  async #render(handle) {
    if (handle === this.#currentHandle) return;
    this.#currentHandle = handle;
    this.dataset.id = handle;

    const cached = this.#cache.get(handle);
    if (cached) {
      this.#swap(cached);
      return;
    }

    this.toggleAttribute('data-loading', true);
    this.#abort?.abort();
    const controller = new AbortController();
    this.#abort = controller;

    try {
      const widgetHTML = await this.#fetchWidget(handle, controller.signal);
      if (controller.signal.aborted || handle !== this.#currentHandle) return;
      if (widgetHTML) {
        this.#cache.set(handle, widgetHTML);
        this.#swap(widgetHTML);
      }
    } catch (error) {
      if (!controller.signal.aborted) {
        console.warn('[bb-reviews] failed to load reviews for', handle, error);
      }
    } finally {
      if (handle === this.#currentHandle) this.removeAttribute('data-loading');
    }
  }

  /**
   * Fetch the product page and extract the rendered Judge.me review LIST
   * (`.jdgm-review-widget`, which wraps `.jdgm-rev-widg`). The reviews come
   * pre-rendered, so they display even where window.jdgm isn't booted; we also
   * copy Judge.me's stylesheet across (once) so it's styled on themes that
   * don't have the app installed (e.g. dev themes).
   * @param {string} handle
   * @param {AbortSignal} signal
   * @returns {Promise<string>}
   */
  async #fetchWidget(handle, signal) {
    const res = await fetch(`/products/${encodeURIComponent(handle)}`, {
      signal,
      headers: { 'X-Requested-With': 'fetch' },
    });
    if (!res.ok) throw new Error(`status ${res.status}`);
    const html = await res.text();
    const doc = new DOMParser().parseFromString(html, 'text/html');
    this.#ensureReviewStyles(doc);
    // Avoid the bare `.jdgm-widget` fallback — on this theme that matches a
    // hidden testimonials carousel, not the reviews.
    const widget = doc.querySelector('.jdgm-review-widget') || doc.querySelector('.jdgm-rev-widg');
    return widget ? widget.outerHTML : '';
  }

  /**
   * Copy Judge.me's stylesheet(s) from a fetched product document into this
   * page's <head> once, so injected review markup is styled even when the
   * Judge.me app isn't present on the current theme.
   * @param {Document} doc
   */
  #ensureReviewStyles(doc) {
    if (BundleReviewsComponent.#stylesInjected) return;
    const head = document.head;
    if (!head) return;
    let injected = false;
    for (const link of doc.querySelectorAll('link[rel="stylesheet"]')) {
      const href = link.getAttribute('href') || '';
      if (!/judge\.me|jdgm/i.test(href)) continue;
      if (document.querySelector(`link[href="${href}"]`)) continue;
      const clone = document.createElement('link');
      clone.rel = 'stylesheet';
      clone.href = href;
      head.appendChild(clone);
      injected = true;
    }
    // Inline jdgm styles (Judge.me ships a big inline block alongside the widget).
    for (const style of doc.querySelectorAll('style')) {
      if (!/jdgm-/.test(style.textContent || '')) continue;
      if (style.textContent.length < 400) continue; // skip tiny unrelated blocks
      const clone = document.createElement('style');
      clone.textContent = style.textContent;
      clone.dataset.bbReviews = 'jdgm';
      head.appendChild(clone);
      injected = true;
      break; // the first substantial jdgm block carries the widget CSS
    }
    if (injected) BundleReviewsComponent.#stylesInjected = true;
  }

  /** Page-wide guard so Judge.me CSS is only copied in once. */
  static #stylesInjected = false;

  /** Custom star rating + review count (gold stars filled to the average). */
  #renderStars(rating, count) {
    const pct = Math.max(0, Math.min(100, (rating / 5) * 100));
    return (
      `<span class="bb-stars" role="img" aria-label="${rating.toFixed(1)} out of 5 stars" style="--bb-star-pct:${pct}%"></span>` +
      `<span class="bb-stars__count">${count} review${count === 1 ? '' : 's'}</span>`
    );
  }

  /**
   * Replace the widget markup and re-init Judge.me so it hydrates the new
   * product's reviews. jdgm boots off `.jdgm-widget[data-product-id]`, so the
   * fresh markup already carries the right id; we just nudge it to load.
   * @param {string} widgetHTML
   */
  #swap(widgetHTML) {
    const { widget } = this.refs;
    if (!widget) return;
    widget.innerHTML = widgetHTML;

    // Judge.me's own JS reveals the widget by adding `jdgm--done-setup-widget`
    // to the review widget — its CSS hides the markup until then and lays it
    // out correctly (histogram as flex, etc.) once present. That JS never runs
    // on our injected copy, so add the class ourselves: this is the clean
    // reveal (no force-display hacks) and matches how the live PDP renders.
    for (const rw of widget.querySelectorAll('.jdgm-review-widget')) {
      rw.classList.add('jdgm--done-setup-widget');
    }

    // Reveal the preview badge (display:none until jdgm reveals it).
    for (const badge of widget.querySelectorAll('.jdgm-prev-badge[style*="display:none"], .jdgm-prev-badge[style*="display: none"]')) {
      badge.style.display = '';
    }

    // Judge.me lazy-loads review/avatar images (real URL in data-src) and
    // requests them at w=160 by default — too small for the displayed size, so
    // it looks pixelated. Bump to w=360&dpr=2 (what the live PDP uses) and do
    // the swap ourselves since jdgm's lazy-loader doesn't run here.
    for (const img of widget.querySelectorAll('img[data-src]')) {
      let src = img.getAttribute('data-src');
      if (!src || img.getAttribute('src')) continue;
      src = src.replace(/(\?|&)w=\d+/, '$1w=360');
      if (!/[?&]dpr=/.test(src)) src += (src.includes('?') ? '&' : '?') + 'dpr=2';
      img.setAttribute('src', src);
    }

    // jdgm normally renders each review's date (from data-content) and strips
    // its loading spinners on hydrate. Do both ourselves on the static copy.
    for (const ts of widget.querySelectorAll('.jdgm-rev__timestamp[data-content]')) {
      const raw = ts.getAttribute('data-content');
      const d = raw ? new Date(raw.replace(' UTC', ' GMT')) : null;
      if (d && !Number.isNaN(d.getTime())) {
        ts.textContent = d.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
      }
      ts.classList.remove('jdgm-spinner');
    }
    for (const el of widget.querySelectorAll('.jdgm--loading')) el.classList.remove('jdgm--loading');

    // Verified-buyer badge: jdgm ships an EMPTY `.jdgm-rev__buyer-badge` span and
    // fills it via JS we don't run. Render it ourselves for cards flagged
    // `data-verified-buyer` — jdgm's (copied) CSS already styles the slate pill.
    for (const card of widget.querySelectorAll('.jdgm-rev[data-verified-buyer="true"]')) {
      const badge = card.querySelector('.jdgm-rev__buyer-badge');
      if (badge && !badge.textContent.trim()) badge.textContent = 'Verified Buyer';
    }

    this.#reinitJudgeMe();
  }

  /**
   * Re-initialize Judge.me against the freshly-injected widget. Tries the
   * library's public hooks (versions differ) and falls back to the rebuild
   * event Judge.me listens for.
   */
  #reinitJudgeMe() {
    const w = /** @type {any} */ (window);
    const jdgm = w.jdgm;
    try {
      if (jdgm?.SDK?.batchSpinner) {
        jdgm.SDK.batchSpinner();
      } else if (typeof jdgm?.batchSpinner === 'function') {
        jdgm.batchSpinner();
      } else if (typeof jdgm?.loadReviews === 'function') {
        jdgm.loadReviews();
      } else if (typeof w.jdgmEnableRebuild === 'function') {
        w.jdgmEnableRebuild();
      } else {
        // Judge.me's loader listens for this to (re)scan the page.
        document.dispatchEvent(new CustomEvent('jdgm:init'));
      }
    } catch (error) {
      console.warn('[bb-reviews] judge.me re-init failed', error);
    }
  }
}

if (!customElements.get('bundle-reviews')) {
  customElements.define('bundle-reviews', BundleReviewsComponent);
}
